import { Suspense, useMemo, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import AppHeader from "@/components/AppHeader";
import Main from "@/components/Main";

/**
 * Orbital Shape Gallery.
 *
 * Renders point-cloud isosurfaces for hydrogen-like orbitals using analytical
 * angular functions (real spherical harmonics) multiplied by a simple radial
 * envelope. Phase (sign of ψ) is colored.
 */

type OrbitalKind = {
  key: string;
  label: string;
  n: number;
  l: number;
  /** ml-style label only for display */
  m: string;
  description: string;
};

const ORBITALS: OrbitalKind[] = [
  { key: "1s", label: "1s", n: 1, l: 0, m: "0", description: "Spherical, no nodes. Ground-state H." },
  { key: "2s", label: "2s", n: 2, l: 0, m: "0", description: "Spherical with 1 radial node — a hollow shell inside the cloud." },
  { key: "2px", label: "2pₓ", n: 2, l: 1, m: "x", description: "Two lobes along the x-axis, opposite phase. 1 angular node (yz plane)." },
  { key: "2py", label: "2pᵧ", n: 2, l: 1, m: "y", description: "Two lobes along the y-axis." },
  { key: "2pz", label: "2p_z", n: 2, l: 1, m: "z", description: "Two lobes along the z-axis." },
  { key: "3dz2", label: "3d_z²", n: 3, l: 2, m: "z2", description: "Dumbbell along z with a torus in xy — the only d orbital that looks different." },
  { key: "3dxy", label: "3d_xy", n: 3, l: 2, m: "xy", description: "Four lobes between x and y axes." },
  { key: "3dxz", label: "3d_xz", n: 3, l: 2, m: "xz", description: "Four lobes between x and z axes." },
  { key: "3dyz", label: "3d_yz", n: 3, l: 2, m: "yz", description: "Four lobes between y and z axes." },
  { key: "3dx2y2", label: "3d_x²-y²", n: 3, l: 2, m: "x2-y2", description: "Four lobes ON the x and y axes." },
];

/** Real spherical harmonic-ish angular factor (NOT normalized). */
function angular(kind: OrbitalKind, x: number, y: number, z: number, r: number) {
  switch (kind.m) {
    case "0":
      return 1; // s
    case "x":
      return x / r;
    case "y":
      return y / r;
    case "z":
      return z / r;
    case "xy":
      return (x * y) / (r * r);
    case "xz":
      return (x * z) / (r * r);
    case "yz":
      return (y * z) / (r * r);
    case "x2-y2":
      return (x * x - y * y) / (r * r);
    case "z2":
      return (2 * z * z - x * x - y * y) / (r * r);
    default:
      return 1;
  }
}

/** Radial envelope: cheap exp falloff with a node for n=2 s. */
function radial(kind: OrbitalKind, r: number) {
  const a = 1.5; // Bohr-scale constant
  if (kind.l === 0) {
    if (kind.n === 1) return Math.exp(-r / a);
    if (kind.n === 2) return (1 - r / (2 * a)) * Math.exp(-r / (2 * a));
    return Math.exp(-r / (3 * a));
  }
  if (kind.l === 1) {
    return r * Math.exp(-r / (2 * a));
  }
  // d
  return r * r * Math.exp(-r / (3 * a));
}

/** Generate accept/reject point cloud weighted by |ψ|². */
function generateCloud(kind: OrbitalKind, target = 4000) {
  const points: number[] = [];
  const phases: number[] = []; // +1 / -1
  const range = kind.l === 0 ? 6 : 8;
  let attempts = 0;
  const maxAttempts = target * 80;
  // Estimate a probability scale by sampling a quick max
  let pmax = 0;
  for (let i = 0; i < 400; i++) {
    const x = (Math.random() - 0.5) * 2 * range;
    const y = (Math.random() - 0.5) * 2 * range;
    const z = (Math.random() - 0.5) * 2 * range;
    const r = Math.sqrt(x * x + y * y + z * z) || 0.0001;
    const psi = radial(kind, r) * angular(kind, x, y, z, r);
    const p = psi * psi;
    if (p > pmax) pmax = p;
  }
  pmax = pmax || 1;

  while (points.length / 3 < target && attempts < maxAttempts) {
    attempts++;
    const x = (Math.random() - 0.5) * 2 * range;
    const y = (Math.random() - 0.5) * 2 * range;
    const z = (Math.random() - 0.5) * 2 * range;
    const r = Math.sqrt(x * x + y * y + z * z) || 0.0001;
    const psi = radial(kind, r) * angular(kind, x, y, z, r);
    const p = (psi * psi) / pmax;
    if (Math.random() < p) {
      points.push(x, y, z);
      phases.push(psi >= 0 ? 1 : -1);
    }
  }
  return { points: new Float32Array(points), phases };
}

function OrbitalCloud({ kind, showPhase }: { kind: OrbitalKind; showPhase: boolean }) {
  const { positions, colors } = useMemo(() => {
    const { points, phases } = generateCloud(kind);
    const colorArr = new Float32Array(phases.length * 3);
    for (let i = 0; i < phases.length; i++) {
      if (showPhase && phases[i] < 0) {
        colorArr[i * 3] = 0.95;
        colorArr[i * 3 + 1] = 0.35;
        colorArr[i * 3 + 2] = 0.45;
      } else {
        colorArr[i * 3] = 0.3;
        colorArr[i * 3 + 1] = 0.65;
        colorArr[i * 3 + 2] = 1.0;
      }
    }
    return { positions: points, colors: colorArr };
  }, [kind, showPhase]);

  const geom = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    g.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    return g;
  }, [positions, colors]);

  return (
    <points geometry={geom}>
      <pointsMaterial
        size={0.06}
        vertexColors
        transparent
        opacity={0.85}
        sizeAttenuation
      />
    </points>
  );
}

function Axes({ size = 5 }: { size?: number }) {
  return (
    <group>
      {/* x */}
      <mesh position={[size / 2, 0, 0]} rotation={[0, 0, -Math.PI / 2]}>
        <cylinderGeometry args={[0.01, 0.01, size, 6]} />
        <meshBasicMaterial color="hsl(0 60% 60%)" />
      </mesh>
      {/* y */}
      <mesh position={[0, size / 2, 0]}>
        <cylinderGeometry args={[0.01, 0.01, size, 6]} />
        <meshBasicMaterial color="hsl(140 60% 50%)" />
      </mesh>
      {/* z */}
      <mesh position={[0, 0, size / 2]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.01, 0.01, size, 6]} />
        <meshBasicMaterial color="hsl(210 80% 60%)" />
      </mesh>
    </group>
  );
}

export default function OrbitalGallery() {
  const [activeKey, setActiveKey] = useState(ORBITALS[2].key);
  const orbital = ORBITALS.find((o) => o.key === activeKey)!;
  const [showPhase, setShowPhase] = useState(true);
  const [showAxes, setShowAxes] = useState(true);

  return (
    <div className="min-h-screen bg-transparent">
      <AppHeader />
      <Main className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground">Orbital Shape Gallery</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Real shapes of s, p, and d orbitals — generated from |ψ|² point clouds. Phase
            (sign of the wavefunction) is color-coded so you can see the nodes that explain
            bonding geometry.
          </p>
        </div>

        {/* Orbital tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {ORBITALS.map((o) => (
            <button
              key={o.key}
              onClick={() => setActiveKey(o.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-mono transition-colors ${
                activeKey === o.key
                  ? "bg-foreground text-background"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
          <div className="rounded-lg border border-border bg-tile-bg overflow-hidden h-[480px]">
            <Canvas camera={{ position: [6, 5, 6], fov: 45 }}>
              <Suspense fallback={null}>
                <ambientLight intensity={0.5} />
                <OrbitalCloud kind={orbital} showPhase={showPhase} />
                {showAxes && <Axes size={5} />}
                <OrbitControls enablePan={false} />
              </Suspense>
            </Canvas>
          </div>

          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-tile-bg p-3">
              <h2 className="text-sm font-semibold text-foreground mb-1">{orbital.label}</h2>
              <p className="text-xs font-mono text-muted-foreground mb-3">
                n = {orbital.n} · ℓ = {orbital.l}
              </p>
              <p className="text-xs text-foreground leading-relaxed">{orbital.description}</p>
            </div>
            <div className="rounded-lg border border-border bg-tile-bg p-3 space-y-3">
              <h2 className="text-sm font-semibold text-foreground">View</h2>
              <Toggle label="Show phase (+/−)" value={showPhase} onChange={setShowPhase} />
              <Toggle label="Show axes (x red · y green · z blue)" value={showAxes} onChange={setShowAxes} />
              <div className="pt-2 border-t border-border text-[11px] text-muted-foreground space-y-1">
                <p><span className="inline-block w-2.5 h-2.5 rounded-full bg-[hsl(210_90%_60%)] mr-1.5 align-middle" />ψ &gt; 0</p>
                <p><span className="inline-block w-2.5 h-2.5 rounded-full bg-[hsl(0_85%_60%)] mr-1.5 align-middle" />ψ &lt; 0</p>
                <p className="pt-1">Drag to rotate · scroll to zoom</p>
              </div>
            </div>
          </div>
        </div>
      </Main>
    </div>
  );
}

function Toggle({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between text-xs text-foreground cursor-pointer gap-3">
      <span>{label}</span>
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-primary"
      />
    </label>
  );
}